import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { RegionalVariant, LocationInfo } from "./utils/regionalDetection";
import {
  detectRegionalVariant,
  detectRegionalVariantForLanguage,
  detectLocationInfo,
  isVariantSupported,
  REGIONAL_VARIANTS,
  getVariantsByBaseLanguage,
} from "./utils/regionalDetection";
import { isRTLLanguage } from "./utils/utils";
import { SUPPORTED_LANGUAGES, SUPPORTED_VARIANTS } from "./languages";

export interface I18nState {
  currentLanguage: string;
  currentRegionalCode: string;
  detectedLanguage: string | null;
  storedBrowserLanguage: string | null;
  availableLanguages: string[];
  isLoading: boolean;
  isChangingLanguage: boolean;
  isDetectingVariant: boolean;
  detectionSource: "browser" | "system" | "localStorage" | "fallback" | null;
  currentVariant: RegionalVariant | null;
  detectedVariant: RegionalVariant | null;
  availableVariants: string[];
  variantDetectionSource:
    | "browser-locale"
    | "timezone"
    | "navigator-language"
    | "fallback"
    | null;
  variantDetectionConfidence: number;
  locationInfo: LocationInfo | null;
  isRegionalDetectionEnabled: boolean;
  lastDetectedTimezone: string | null;
  isDetectingTimezone: boolean;
  isRTL: boolean;
  supportedRTLLanguages: string[];
  error: string | null;
}

const initialState: I18nState = {
  currentLanguage: "en",
  currentRegionalCode: "en-US",
  detectedLanguage: null,
  storedBrowserLanguage: null,
  availableLanguages: [...SUPPORTED_LANGUAGES],
  isLoading: false,
  isChangingLanguage: false,
  isDetectingVariant: false,
  detectionSource: null,
  currentVariant: null,
  detectedVariant: null,
  availableVariants: [...SUPPORTED_VARIANTS],
  variantDetectionSource: null,
  variantDetectionConfidence: 0,
  locationInfo: null,
  isRegionalDetectionEnabled: true,
  lastDetectedTimezone: null,
  isDetectingTimezone: false,
  isRTL: false,
  supportedRTLLanguages: [
    "ar",
    "he",
    "fa",
    "ur",
    "ku",
    "dv",
    "sd",
    "ckb",
    "arc",
    "ji",
    "syr",
  ],
  error: null,
};

export const initializeI18n = createAsyncThunk(
  "i18n/initialize",
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const {
      availableVariants,
      availableLanguages,
      isRegionalDetectionEnabled,
      currentLanguage,
      storedBrowserLanguage,
    } = state.i18n;

    const currentBrowserLanguage =
      navigator.language || navigator.languages?.[0] || "en";

    // Check if user has explicitly chosen a language/variant
    const storedLng = localStorage.getItem("i18nextLng");
    const isRegionalVariant = storedLng && storedLng.includes("-");
    const hasUserChoice = storedLng && (
      isRegionalVariant 
        ? availableVariants.includes(storedLng)
        : availableLanguages.includes(storedLng)
    );

    if (hasUserChoice && isRegionalVariant) {
      // Handle regional variant directly
      const variant = REGIONAL_VARIANTS[storedLng];
      if (variant) {
        dispatch(setCurrentVariant(variant));
        dispatch(setLanguage(variant.baseLanguage));
        dispatch(setCurrentRegionalCode(storedLng));
        dispatch(setIsRTL(isRTLLanguage(variant.baseLanguage)));
        return { variant, baseLanguage: variant.baseLanguage };
      }
    } else if (hasUserChoice) {
      // Handle base language
      dispatch(setIsRTL(isRTLLanguage(storedLng)));
    }

    if (isRegionalDetectionEnabled && !hasUserChoice) {
      // Only run detection if user hasn't made an explicit choice
      const shouldRunDetection =
        !storedBrowserLanguage ||
        storedBrowserLanguage !== currentBrowserLanguage;

      if (shouldRunDetection) {
        dispatch(setStoredBrowserLanguage(currentBrowserLanguage));

        const result = detectRegionalVariant(
          availableVariants,
          availableLanguages
        );
        const locationInfo = detectLocationInfo();

        dispatch(setLocationInfo(locationInfo));
        dispatch(
          setDetectedVariant({
            variant: result.variant,
            source: result.source as any,
            confidence: result.confidence,
          })
        );
        dispatch(setCurrentVariant(result.variant));
        dispatch(setCurrentRegionalCode(result.variant.code));

        const baseLanguage = result.variant.baseLanguage;
        if (
          availableLanguages.includes(baseLanguage) &&
          baseLanguage !== currentLanguage
        ) {
          dispatch(setLanguage(baseLanguage));
          dispatch(setIsRTL(isRTLLanguage(baseLanguage)));
          const { default: i18n } = await import("./i18n");

          if (i18n.language !== result.variant.code) {
            await i18n.changeLanguage(result.variant.code);
          }
        }

        return result;
      }
    } else if (hasUserChoice && !isRegionalVariant) {
      // Ensure the variant is set correctly for the user's language
      const variantForLanguage = Object.values(REGIONAL_VARIANTS).find(
        (v) => v.baseLanguage === storedLng
      );
      if (variantForLanguage) {
        dispatch(setCurrentVariant(variantForLanguage));
        dispatch(setCurrentRegionalCode(variantForLanguage.code));
      }
    }

    return null;
  }
);

export const detectVariantForLanguage = createAsyncThunk(
  "i18n/detectVariantForLanguage",
  async (language: string, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { availableVariants } = state.i18n;

    const availableVariantsForLanguage = getVariantsByBaseLanguage(
      language
    ).filter((variant) => availableVariants.includes(variant.code));

    if (availableVariantsForLanguage.length === 0) {
      return null;
    }

    const locationInfo = detectLocationInfo();

    const result = detectRegionalVariantForLanguage(
      language,
      availableVariantsForLanguage.map((v) => v.code)
    );

    dispatch(setLocationInfo(locationInfo));
    dispatch(
      setDetectedVariant({
        variant: result.variant,
        source: result.source as any,
        confidence: result.confidence,
      })
    );
    dispatch(setCurrentVariant(result.variant));
    dispatch(setCurrentRegionalCode(result.variant.code));

    return result;
  }
);

export const changeLanguage = createAsyncThunk<string, string>(
  "i18n/changeLanguage",
  async (language: string, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { availableLanguages, availableVariants, currentLanguage } = state.i18n;

    // Check if the language is a regional variant
    const isRegionalVariant = language.includes("-");
    
    if (isRegionalVariant) {
      if (!availableVariants.includes(language)) {
        throw new Error(`Variant ${language} is not supported`);
      }
      const result = await dispatch(switchToVariant(language)).unwrap();
      return result.baseLanguage;
    } else {
      if (!availableLanguages.includes(language)) {
        throw new Error(`Language ${language} is not supported`);
      }

      if (language === currentLanguage) {
        return language;
      }

      const { default: i18n } = await import("./i18n");
      
      const result = await dispatch(detectVariantForLanguage(language)).unwrap();
      const regionalCode = result?.variant.code || language;
      
      if (i18n.language !== regionalCode) {
        await i18n.changeLanguage(regionalCode);
      }

      dispatch(setIsRTL(isRTLLanguage(language)));
      dispatch(setCurrentRegionalCode(regionalCode));

      return language;
    }
  }
);

export const detectAndSetRegionalVariant = createAsyncThunk(
  "i18n/detectAndSetRegionalVariant",
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const {
      availableVariants,
      availableLanguages,
      currentLanguage,
      storedBrowserLanguage,
    } = state.i18n;

    const currentBrowserLanguage =
      navigator.language || navigator.languages?.[0] || "en";
    const shouldRunDetection =
      !storedBrowserLanguage ||
      storedBrowserLanguage !== currentBrowserLanguage;
    if (shouldRunDetection) {
      dispatch(setStoredBrowserLanguage(currentBrowserLanguage));

      const result = detectRegionalVariant(
        availableVariants,
        availableLanguages
      );
      const locationInfo = detectLocationInfo();

      dispatch(setLocationInfo(locationInfo));
      dispatch(
        setDetectedVariant({
          variant: result.variant,
          source: result.source as any,
          confidence: result.confidence,
        })
      );
      dispatch(setCurrentRegionalCode(result.variant.code));

      const baseLanguage = result.variant.baseLanguage;
      if (
        availableLanguages.includes(baseLanguage) &&
        baseLanguage !== currentLanguage
      ) {
        await dispatch(changeLanguage(result.variant.code));
      }

      return result;
    }

    return null;
  }
);

export const detectAndUpdateTimezone = createAsyncThunk(
  "i18n/detectAndUpdateTimezone",
  async (_, { dispatch }) => {
    const TIMEZONE_STORAGE_KEY = "app_detected_timezone";

    const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const storedTimezone = localStorage.getItem(TIMEZONE_STORAGE_KEY);

    const hasTimezoneChanged = storedTimezone !== currentTimezone;

    if (hasTimezoneChanged) {
      localStorage.setItem(TIMEZONE_STORAGE_KEY, currentTimezone);

      const newLocationInfo = detectLocationInfo();
      dispatch(setLocationInfo(newLocationInfo));
    }

    return {
      currentTimezone,
      previousTimezone: storedTimezone,
      hasChanged: hasTimezoneChanged,
      locationInfo: detectLocationInfo(),
    };
  }
);

export const switchToVariant = createAsyncThunk(
  "i18n/switchToVariant",
  async (variantCode: string, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { availableVariants, availableLanguages, currentLanguage } =
      state.i18n;

    if (!isVariantSupported(variantCode, availableVariants)) {
      throw new Error(`Variant ${variantCode} is not supported`);
    }

    const variant = REGIONAL_VARIANTS[variantCode];
    if (!variant) {
      throw new Error(`Variant ${variantCode} not found in registry`);
    }

    const baseLanguage = variant.baseLanguage;
    if (!availableLanguages.includes(baseLanguage)) {
      throw new Error(`Base language ${baseLanguage} is not supported`);
    }

    if (baseLanguage !== currentLanguage) {
      dispatch(setLanguage(baseLanguage));
      dispatch(setIsRTL(isRTLLanguage(baseLanguage)));
    }

    const { default: i18n } = await import("./i18n");
    if (i18n.language !== variantCode) {
      await i18n.changeLanguage(variantCode);
    }

    dispatch(setCurrentVariant(variant));
    dispatch(setCurrentRegionalCode(variantCode));

    return { variant, baseLanguage };
  }
);

export const i18nSlice = createSlice({
  name: "i18n",
  initialState,
  reducers: {
    setLanguage: (state, action: PayloadAction<string>) => {
      state.currentLanguage = action.payload;
      state.detectionSource = "localStorage";
      state.isRTL = isRTLLanguage(action.payload);
    },
    setCurrentRegionalCode: (state, action: PayloadAction<string>) => {
      state.currentRegionalCode = action.payload;
    },
    setDetectedLanguage: (
      state,
      action: PayloadAction<{
        language: string;
        source: "browser" | "system" | "localStorage" | "fallback";
      }>
    ) => {
      state.detectedLanguage = action.payload.language;
      state.detectionSource = action.payload.source;
    },
    setAvailableLanguages: (state, action: PayloadAction<string[]>) => {
      state.availableLanguages = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setCurrentVariant: (state, action: PayloadAction<RegionalVariant>) => {
      state.currentVariant = action.payload;
    },
    setDetectedVariant: (
      state,
      action: PayloadAction<{
        variant: RegionalVariant;
        source:
          | "browser-locale"
          | "timezone"
          | "navigator-language"
          | "fallback";
        confidence: number;
      }>
    ) => {
      state.detectedVariant = action.payload.variant;
      state.variantDetectionSource = action.payload.source;
      state.variantDetectionConfidence = action.payload.confidence;
    },
    setAvailableVariants: (state, action: PayloadAction<string[]>) => {
      state.availableVariants = action.payload;
    },
    setLocationInfo: (state, action: PayloadAction<LocationInfo | null>) => {
      state.locationInfo = action.payload;
    },
    setRegionalDetectionEnabled: (state, action: PayloadAction<boolean>) => {
      state.isRegionalDetectionEnabled = action.payload;
    },
    setIsRTL: (state, action: PayloadAction<boolean>) => {
      state.isRTL = action.payload;
    },
    setSupportedRTLLanguages: (state, action: PayloadAction<string[]>) => {
      state.supportedRTLLanguages = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setStoredBrowserLanguage: (state, action: PayloadAction<string>) => {
      state.storedBrowserLanguage = action.payload;
    },
    resetI18nState: (state) => {
      state.currentLanguage = "en";
      state.currentRegionalCode = "en-US";
      state.detectedLanguage = null;
      state.storedBrowserLanguage = null;
      state.availableLanguages = [...SUPPORTED_LANGUAGES];
      state.isLoading = false;
      state.isChangingLanguage = false;
      state.isDetectingVariant = false;
      state.detectionSource = null;
      state.currentVariant = null;
      state.detectedVariant = null;
      state.availableVariants = [...SUPPORTED_VARIANTS];
      state.variantDetectionSource = null;
      state.variantDetectionConfidence = 0;
      state.locationInfo = null;
      state.isRegionalDetectionEnabled = true;
      state.lastDetectedTimezone = null;
      state.isDetectingTimezone = false;
      state.isRTL = false;
      state.supportedRTLLanguages = [
        "ar",
        "he",
        "fa",
        "ur",
        "ku",
        "dv",
        "sd",
        "ckb",
        "arc",
        "ji",
        "syr",
      ];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeI18n.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeI18n.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.currentVariant = action.payload.variant;
        }
        state.error = null;
      })
      .addCase(initializeI18n.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to initialize i18n";
      })
      .addCase(changeLanguage.pending, (state) => {
        state.isChangingLanguage = true;
        state.error = null;
      })
      .addCase(changeLanguage.fulfilled, (state, action) => {
        state.isChangingLanguage = false;
        state.currentLanguage = action.payload;
        state.error = null;
      })
      .addCase(changeLanguage.rejected, (state, action) => {
        state.isChangingLanguage = false;
        state.error = action.error.message || "Failed to change language";
      })
      .addCase(detectVariantForLanguage.pending, (state) => {
        state.isDetectingVariant = true;
        state.error = null;
      })
      .addCase(detectVariantForLanguage.fulfilled, (state, action) => {
        state.isDetectingVariant = false;
        if (action.payload) {
          state.currentVariant = action.payload.variant;
        }
        state.error = null;
      })
      .addCase(detectVariantForLanguage.rejected, (state, action) => {
        state.isDetectingVariant = false;
        state.error =
          action.error.message || "Failed to detect variant for language";
      })
      .addCase(detectAndSetRegionalVariant.pending, (state) => {
        state.isDetectingVariant = true;
        state.error = null;
      })
      .addCase(detectAndSetRegionalVariant.fulfilled, (state, action) => {
        state.isDetectingVariant = false;
        if (action.payload) {
          state.currentVariant = action.payload.variant;
        }
        state.error = null;
      })
      .addCase(detectAndSetRegionalVariant.rejected, (state, action) => {
        state.isDetectingVariant = false;
        state.error =
          action.error.message || "Failed to detect regional variant";
      })
      .addCase(detectAndUpdateTimezone.pending, (state) => {
        state.isDetectingTimezone = true;
        state.error = null;
      })
      .addCase(detectAndUpdateTimezone.fulfilled, (state, action) => {
        state.isDetectingTimezone = false;
        state.lastDetectedTimezone = action.payload.currentTimezone;
        state.locationInfo = action.payload.locationInfo;
        state.error = null;
      })
      .addCase(detectAndUpdateTimezone.rejected, (state, action) => {
        state.isDetectingTimezone = false;
        state.error =
          action.error.message || "Failed to detect and update timezone";
      })
      .addCase(switchToVariant.pending, (state) => {
        state.isChangingLanguage = true;
        state.error = null;
      })
      .addCase(switchToVariant.fulfilled, (state, action) => {
        state.isChangingLanguage = false;
        state.currentVariant = action.payload.variant;
        state.currentLanguage = action.payload.baseLanguage;
        state.error = null;
      })
      .addCase(switchToVariant.rejected, (state, action) => {
        state.isChangingLanguage = false;
        state.error = action.error.message || "Failed to switch variant";
      });
  },
});

export const {
  setLanguage,
  setDetectedLanguage,
  setAvailableLanguages,
  setLoading,
  setCurrentVariant,
  setDetectedVariant,
  setAvailableVariants,
  setLocationInfo,
  setRegionalDetectionEnabled,
  setIsRTL,
  setSupportedRTLLanguages,
  clearError,
  setStoredBrowserLanguage,
  resetI18nState,
  setCurrentRegionalCode,
} = i18nSlice.actions;

export const selectCurrentLanguage = (state: RootState) =>
  state.i18n.currentLanguage;
export const selectDetectedLanguage = (state: RootState) =>
  state.i18n.detectedLanguage;
export const selectDetectionSource = (state: RootState) =>
  state.i18n.detectionSource;
export const selectAvailableLanguages = (state: RootState) =>
  state.i18n.availableLanguages;
export const selectI18nLoading = (state: RootState) => state.i18n.isLoading;
export const selectIsChangingLanguage = (state: RootState) =>
  state.i18n.isChangingLanguage;
export const selectIsDetectingVariant = (state: RootState) =>
  state.i18n.isDetectingVariant;
export const selectCurrentVariant = (state: RootState) =>
  state.i18n.currentVariant;
export const selectDetectedVariant = (state: RootState) =>
  state.i18n.detectedVariant;
export const selectAvailableVariants = (state: RootState) =>
  state.i18n.availableVariants;
export const selectVariantDetectionSource = (state: RootState) =>
  state.i18n.variantDetectionSource;
export const selectVariantDetectionConfidence = (state: RootState) =>
  state.i18n.variantDetectionConfidence;
export const selectLocationInfo = (state: RootState) => state.i18n.locationInfo;
export const selectIsRegionalDetectionEnabled = (state: RootState) =>
  state.i18n.isRegionalDetectionEnabled;
export const selectLastDetectedTimezone = (state: RootState) =>
  state.i18n.lastDetectedTimezone;
export const selectTimeZoneFromLocationInfo = (state: RootState) =>
  state.i18n.locationInfo?.timezone;
export const selectIsDetectingTimezone = (state: RootState) =>
  state.i18n.isDetectingTimezone;
export const selectI18nError = (state: RootState) => state.i18n.error;
export const selectStoredBrowserLanguage = (state: RootState) =>
  state.i18n.storedBrowserLanguage;
export const selectIsRTL = (state: RootState) => state.i18n.isRTL;
export const selectSupportedRTLLanguages = (state: RootState) =>
  state.i18n.supportedRTLLanguages;
export const selectCurrentRegionalCode = (state: RootState) =>
  state.i18n.currentRegionalCode;

export default i18nSlice.reducer;
